function hexToRGB(hex: string, alpha: number) {
	const r = parseInt(hex.slice(1, 3), 16),
		g = parseInt(hex.slice(3, 5), 16),
		b = parseInt(hex.slice(5, 7), 16);

	if (alpha) {
		return "rgba(" + r + ", " + g + ", " + b + ", " + alpha + ")";
	} else {
		return "rgb(" + r + ", " + g + ", " + b + ")";
	}
}

function adjust(color: string, amount: number) {
	return (
		"#" +
		color
			.replace(/^#/, "")
			.replace(/../g, (hex) => ("0" + Math.min(255, Math.max(0, parseInt(hex, 16) + amount)).toString(16)).slice(-2))
	);
}

function getRandomColor() {
	const letters = "0123456789ABCDEF";
	let color = "#";
	for (let i = 0; i < 6; i++) {
		color += letters[Math.floor(Math.random() * 16)];
	}
	return color;
}

export function colorGenerator(
	index: number,
	colors: string[] = [
		"#00AEEF",
		"#C63450",
		"#F26D2D",
		"#007CB7",
		"#FFBF00",
		"#EA401F",
		"#3C51B5",
		"#4CB09C",
		"#F46782",
		"#8C8EA8",
	],
): string {
	let generatedColor: string;
	const shadowLevels = 4;
	const maxShadow = 80;
	const colorLen = colors.length;

	if (index < colorLen * shadowLevels) {
		const colorLevel = Math.floor(index / colorLen);
		const colorIndex = index - colorLevel * colorLen;
		generatedColor = adjust(colors[colorIndex], -(colorLevel * maxShadow) / shadowLevels);
	} else {
		generatedColor = getRandomColor();
	}

	return generatedColor;
}

const stableColorGeneratorMap = new Map</* name */ string, /* color */ string>([
	["EQUITY", "#00AEEF"],
	["FIXED-INCOME", "#FFBF00"],
	["FIXED_INCOME", "#FFBF00"],
	["COMMODITIES", "#F26D2D"],
	["ALTERNATIVE", "#C63450"],
	["MONEY-MARKET", "#3C51B5"],

	["Equity", "#00AEEF"],
	["Fixed Income", "#FFBF00"],
	["Commodities", "#F26D2D"],
	["Alternative", "#C63450"],
	["Money Market", "#3C51B5"],

	["equity", "#00AEEF"],
	["fixedIncome", "#FFBF00"],
	["commodities", "#F26D2D"],
	["alternative", "#C63450"],
	["moneyMarket", "#3C51B5"],

	["Portfolio", "#00AEEF"],
	["Portfolio Benchmark", "#697796"],
]);

const stableColorGeneratorColors = [
	"#00AEEF",
	"#C63450",
	"#F26D2D",
	"#007CB7",
	"#FFBF00",
	"#EA401F",
	"#3C51B5",
	"#4CB09C",
	"#F46782",
	"#8C8EA8",
];

/**
 * Collapses the spellings the map above carries for one concept — `EQUITY`, `Equity`, `equity`,
 * `FIXED-INCOME`, `FIXED_INCOME`, `Fixed Income`, `fixedIncome` — onto a single key, so a lookup
 * doesn't have to guess which casing a caller happens to use. Uppercases, splits camelCase, and
 * treats `-`/`_`/whitespace as the same separator. Every group of spellings in the map already
 * shares one colour, so this can't make the lookup ambiguous.
 */
function conceptKey(key: string): string {
	return key
		.replace(/([a-z0-9])([A-Z])/g, "$1 $2")
		.replace(/[-_\s]+/g, " ")
		.trim()
		.toUpperCase();
}

/**
 * The CURATED concept→colour pairs, snapshotted before anything can mutate the map above.
 *
 * The snapshot is the point, not an optimisation: `stableColorGenerator` adds entries at runtime for
 * keys it has never seen, with colours it mints itself. Those are not agreed meanings, and a lookup
 * that answered with one would be reporting an accident as a decision. Reading a frozen copy of the
 * hand-written table keeps the answer deterministic and independent of call order.
 */
const curatedStableColors: ReadonlyMap<string, string> = new Map(
	Array.from(stableColorGeneratorMap).flatMap(([key, color]) => [
		[key, color] as const,
		[conceptKey(key), color] as const,
	]),
);

/**
 * The colour this product already gives a named concept ("Equity", "Fixed Income", …), or
 * `undefined` when the name carries no agreed meaning — leaving the fallback to the caller.
 *
 * Use this, and NOT {@link stableColorGenerator}, for free-form names: that function has a side
 * effect (see `curatedStableColors`) and always returns something, so it can't tell you whether a
 * name means anything.
 */
export function knownStableColor(key: string): string | undefined {
	return curatedStableColors.get(key) ?? curatedStableColors.get(conceptKey(key));
}

export function stableColorGenerator(key: string, opacity?: number): string {
	const candidate = stableColorGeneratorMap.get(key);

	if (candidate) {
		return opacity ? hexToRGB(candidate, opacity) : candidate;
	}

	const generatedColor = colorGenerator(stableColorGeneratorMap.size, stableColorGeneratorColors);
	stableColorGeneratorMap.set(key, generatedColor);

	return opacity ? hexToRGB(generatedColor, opacity) : generatedColor;
}
